// MIDAS V3 — Auto-resize composer textarea (Phase 2)
//
// Ports `AutoResizeTextarea` from the design handoff:
//   ToDo/MidasChat/Claude_Design_files/machineid/design_handoff_midas_v3/
//   chat-window.jsx (lines 502-532)
//
// Behaviour (spec §8):
//   • line-height 20px, padding 4px 0, starts at 28px total
//   • onChange: el.style.height = "auto"; then min(scrollHeight, 6*20+8)
//   • Enter sends, Shift+Enter inserts newline (default; do NOT preventDefault)
//   • Internal scroll once content exceeds 6 lines (overflowY: auto)
//   • Reset to 28px after send (handled by parent via the imperative ref)
//
// No MUI, no @emotion, no `sx`, no `dt` tokens. Plain <textarea> styled via
// inline styles + var(--*) tokens from the v3 token sheet.
import React, {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";

const LINE_HEIGHT_PX = 20;
const MAX_ROWS = 6;
const PADDING_VERTICAL_TOTAL = 8; // 4px top + 4px bottom
const INITIAL_HEIGHT_PX = LINE_HEIGHT_PX + PADDING_VERTICAL_TOTAL; // 28
const MAX_HEIGHT_PX = LINE_HEIGHT_PX * MAX_ROWS + PADDING_VERTICAL_TOTAL; // 128

export interface AutoResizeTextareaV3Handle {
  /** Reset the textarea height to its single-line baseline (called after send). */
  reset: () => void;
  /** Focus the underlying <textarea>. */
  focus: () => void;
}

export interface AutoResizeTextareaV3Props {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  maxLength?: number;
}

const AutoResizeTextareaV3 = forwardRef<
  AutoResizeTextareaV3Handle,
  AutoResizeTextareaV3Props
>(function AutoResizeTextareaV3Inner(
  { value, onChange, onSubmit, placeholder, disabled, ariaLabel, maxLength },
  ref
) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Resize after every value change. useLayoutEffect avoids a one-frame flash
  // of the wrong height when text is pasted or programmatically set.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, MAX_HEIGHT_PX);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT_PX ? "auto" : "hidden";
  }, [value]);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = `${INITIAL_HEIGHT_PX}px`;
        el.style.overflowY = "hidden";
      },
      focus: () => {
        textareaRef.current?.focus();
      },
    }),
    []
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter inserts a newline (default browser behaviour).
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
    // Shift+Enter → fall through, do NOT preventDefault.
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    if (maxLength != null && next.length > maxLength) return;
    onChange(next);
  };

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      rows={1}
      aria-label={ariaLabel}
      style={{
        width: "100%",
        minHeight: INITIAL_HEIGHT_PX,
        maxHeight: MAX_HEIGHT_PX,
        height: INITIAL_HEIGHT_PX,
        lineHeight: `${LINE_HEIGHT_PX}px`,
        padding: "4px 0",
        margin: 0,
        border: 0,
        outline: "none",
        resize: "none",
        background: "transparent",
        color: "var(--ink)",
        fontFamily: "var(--font-sans)",
        fontSize: 14,
        letterSpacing: "-0.005em",
        // overflowY is set imperatively by the layout effect above.
        overflowY: "hidden",
      }}
    />
  );
});

export default AutoResizeTextareaV3;
